/**
 * Sol-Ark Settings Recommendation Engine
 * 
 * Computes recommended inverter settings based on user inputs
 * about their system configuration.
 */

export interface SystemProfile {
  // Battery configuration
  batteryType: 'lithium' | 'lead-acid' | 'agm' | 'gel';
  batteryCapacityAh: number;
  batteryVoltage: 48 | 24 | 12;
  batteryCount: number;
  
  // Generator
  hasGenerator: boolean;
  generatorAutoStart: boolean;
  
  // Grid configuration
  gridTied: boolean;
  zeroExport: boolean;
  
  // Usage preferences
  reservePercent: number; // Shutdown threshold
  prioritizeSelfConsumption: boolean;
}

export interface RecommendedSettings {
  // Battery
  battType: number;
  batteryCap: number;
  batteryMaxCurrentCharge: number;
  batteryMaxCurrentDischarge: number;
  batteryShutdownVolt: number;
  batteryRestartVolt: number;
  batteryLowVolt: number;
  floatVolt: number;
  absorptionVolt: number;
  lithiumMode?: number;
  
  // Generator (optional)
  generatorStartVolt?: number;
  generatorStartCap?: number;
  genOnVolt?: number;
  genOffVolt?: number;
  genChargeOn?: number;
  genMinSolar?: number;
  
  // Grid
  zeroExportPower?: number;
  gridAlwaysOn?: number;
}

/**
 * Battery voltage curves based on type
 */
const BATTERY_PROFILES = {
  'lithium': {
    battType: 1,
    floatMultiplier: 1.0833,  // ~52V for 48V system
    absorptionMultiplier: 1.125, // ~54V for 48V system
    shutdownMultiplier: 0.9167,  // ~44V for 48V system (20% reserve)
    restartMultiplier: 0.9583,   // ~46V for 48V system
    lowVoltMultiplier: 0.9375,   // ~45V for 48V system
    chargeRate: 0.5,  // 0.5C typical
    dischargeRate: 1.0, // 1C typical
  },
  'lead-acid': {
    battType: 0,
    floatMultiplier: 1.1,     // ~52.8V for 48V system
    absorptionMultiplier: 1.175, // ~56.4V for 48V system
    shutdownMultiplier: 0.875,   // ~42V for 48V system
    restartMultiplier: 0.9375,   // ~45V for 48V system
    lowVoltMultiplier: 0.9167,   // ~44V for 48V system
    chargeRate: 0.2,  // C/5 typical
    dischargeRate: 0.5, // C/2 typical
  },
  'agm': {
    battType: 2,
    floatMultiplier: 1.0917,
    absorptionMultiplier: 1.1583,
    shutdownMultiplier: 0.875,
    restartMultiplier: 0.9375,
    lowVoltMultiplier: 0.9167,
    chargeRate: 0.25,
    dischargeRate: 0.5,
  },
  'gel': {
    battType: 3,
    floatMultiplier: 1.0833,
    absorptionMultiplier: 1.1417,
    shutdownMultiplier: 0.875,
    restartMultiplier: 0.9375,
    lowVoltMultiplier: 0.9167,
    chargeRate: 0.2,
    dischargeRate: 0.4,
  },
} as const;

/**
 * Compute recommended settings based on system profile
 */
export function computeRecommendations(profile: SystemProfile): RecommendedSettings {
  const batteryProfile = BATTERY_PROFILES[profile.batteryType];
  const nominalVoltage = profile.batteryVoltage;
  const totalCapacityAh = profile.batteryCapacityAh * profile.batteryCount;
  
  // Calculate voltages based on battery type and nominal voltage
  const floatVolt = Math.round(nominalVoltage * batteryProfile.floatMultiplier * 10) / 10;
  const absorptionVolt = Math.round(nominalVoltage * batteryProfile.absorptionMultiplier * 10) / 10;
  
  // Adjust shutdown voltage based on reserve percent
  // Lower reserve = lower shutdown voltage
  const reserveFactor = 1 - (profile.reservePercent / 100);
  const shutdownVolt = Math.round(nominalVoltage * (batteryProfile.shutdownMultiplier + (0.1 * reserveFactor)) * 10) / 10;
  const restartVolt = Math.round(shutdownVolt + (nominalVoltage * 0.04) * 10) / 10;
  const lowVolt = Math.round((shutdownVolt + restartVolt) / 2 * 10) / 10;
  
  // Calculate charge/discharge currents
  const maxChargeCurrent = Math.round(totalCapacityAh * batteryProfile.chargeRate);
  const maxDischargeCurrent = Math.round(totalCapacityAh * batteryProfile.dischargeRate);
  
  const settings: RecommendedSettings = {
    battType: batteryProfile.battType,
    batteryCap: totalCapacityAh,
    batteryMaxCurrentCharge: Math.min(maxChargeCurrent, 200), // Cap at 200A
    batteryMaxCurrentDischarge: Math.min(maxDischargeCurrent, 200),
    batteryShutdownVolt: shutdownVolt,
    batteryRestartVolt: restartVolt,
    batteryLowVolt: lowVolt,
    floatVolt: floatVolt,
    absorptionVolt: absorptionVolt,
  };
  
  // Lithium-specific settings
  if (profile.batteryType === 'lithium') {
    settings.lithiumMode = 1;
  }
  
  // Generator settings
  if (profile.hasGenerator) {
    // Start generator when battery drops to reserve + 5%
    settings.generatorStartCap = profile.reservePercent + 5;
    settings.generatorStartVolt = shutdownVolt + 2;
    
    // Stop generator when battery reaches 80%
    settings.genOnVolt = Math.round(nominalVoltage * 1.05 * 10) / 10;
    settings.genOffVolt = Math.round(nominalVoltage * 1.075 * 10) / 10;
    
    settings.genChargeOn = profile.generatorAutoStart ? 1 : 0;
    settings.genMinSolar = 500; // Don't start gen if solar > 500W
  }
  
  // Grid settings
  if (!profile.gridTied || profile.zeroExport) {
    settings.zeroExportPower = 0;
  }
  
  if (profile.gridTied) {
    settings.gridAlwaysOn = 1;
  } else {
    settings.gridAlwaysOn = 0;
  }
  
  return settings;
}

/**
 * Built-in configuration presets
 */
export const BUILT_IN_PRESETS = {
  'off-grid-optimized': {
    name: 'Off-Grid Optimized',
    description: 'Maximizes battery life and generator efficiency for off-grid systems',
    icon: 'Zap',
    profile: {
      gridTied: false,
      zeroExport: true,
      prioritizeSelfConsumption: true,
      hasGenerator: true,
      generatorAutoStart: true,
      reservePercent: 20,
    } as Partial<SystemProfile>,
  },
  'grid-tied-max-export': {
    name: 'Grid-Tied Max Export',
    description: 'Maximizes solar export to grid while maintaining battery backup',
    icon: 'ArrowUpRight',
    profile: {
      gridTied: true,
      zeroExport: false,
      prioritizeSelfConsumption: false,
      hasGenerator: false,
      reservePercent: 10,
    } as Partial<SystemProfile>,
  },
  'self-consumption': {
    name: 'Self-Consumption',
    description: 'Prioritizes using solar for home loads, minimal grid interaction',
    icon: 'Home',
    profile: {
      gridTied: true,
      zeroExport: true,
      prioritizeSelfConsumption: true,
      hasGenerator: false,
      reservePercent: 15,
    } as Partial<SystemProfile>,
  },
  'storm-mode': {
    name: 'Storm Preparation',
    description: 'Charges battery to 100% and increases reserve for extended outage',
    icon: 'CloudLightning',
    profile: {
      prioritizeSelfConsumption: false,
      reservePercent: 30,
      hasGenerator: true,
      generatorAutoStart: true,
    } as Partial<SystemProfile>,
  },
  'battery-saver': {
    name: 'Battery Saver',
    description: 'Reduces charge/discharge rates to extend battery lifespan',
    icon: 'Battery',
    profile: {
      prioritizeSelfConsumption: true,
      reservePercent: 25,
    } as Partial<SystemProfile>,
  },
} as const;

/**
 * Compare two settings objects and return differences
 */
export function diffSettings(
  current: Record<string, unknown>,
  recommended: Record<string, unknown>
): { field: string; current: unknown; recommended: unknown }[] {
  const diffs: { field: string; current: unknown; recommended: unknown }[] = [];
  
  for (const [key, newValue] of Object.entries(recommended)) {
    const currentValue = current[key];
    
    // Compare with type coercion for numbers
    if (String(currentValue) !== String(newValue)) {
      diffs.push({
        field: key,
        current: currentValue,
        recommended: newValue,
      });
    }
  }
  
  return diffs;
}

/**
 * Human-readable labels for settings fields
 */
export const SETTINGS_LABELS: Record<string, string> = {
  battType: 'Battery Type',
  batteryCap: 'Battery Capacity (Ah)',
  batteryMaxCurrentCharge: 'Max Charge Current (A)',
  batteryMaxCurrentDischarge: 'Max Discharge Current (A)',
  batteryShutdownVolt: 'Shutdown Voltage (V)',
  batteryRestartVolt: 'Restart Voltage (V)',
  batteryLowVolt: 'Low Battery Voltage (V)',
  floatVolt: 'Float Voltage (V)',
  absorptionVolt: 'Absorption Voltage (V)',
  lithiumMode: 'Lithium Mode',
  generatorStartVolt: 'Gen Start Voltage (V)',
  generatorStartCap: 'Gen Start SOC (%)',
  genOnVolt: 'Gen On Voltage (V)',
  genOffVolt: 'Gen Off Voltage (V)',
  genChargeOn: 'Gen Auto-Charge',
  genMinSolar: 'Min Solar for Gen (W)',
  zeroExportPower: 'Zero Export Power (W)',
  gridAlwaysOn: 'Grid Always On',
};

/**
 * Format a settings value for display
 */
export function formatSettingValue(field: string, value: unknown): string {
  if (value === undefined || value === null) return '—';
  
  // Boolean-like fields
  if (field === 'genChargeOn' || field === 'gridAlwaysOn' || field === 'lithiumMode') {
    return value === 1 || value === true ? 'Enabled' : 'Disabled';
  }
  
  // Battery type
  if (field === 'battType') {
    const types = ['Lead-Acid', 'Lithium', 'AGM', 'Gel'];
    return types[Number(value)] || String(value);
  }
  
  return String(value);
}
